/**
 * Accessibility Widget Trigger
 *
 * Hides the UserWay floating launcher (footer-only entry via API).
 * Listens for click/keyboard on [data-accessibility-widget-trigger] and opens
 * the UserWay panel (widgetOpen). Uses event delegation; works when UserWay loads async.
 *
 * @module accessibility-widget
 * @see layout/theme.liquid — UserWay script (cdn.userway.org/widget.js)
 * @see blocks/menu.liquid — [data-accessibility-widget-trigger] span(s)
 */

const TRIGGER_SELECTOR = '[data-accessibility-widget-trigger]';
const RETRY_DELAY_MS = 300;
const ICON_HIDE_POLL_MS = 150;
const ICON_HIDE_MAX_ATTEMPTS = 40;

/**
 * Hide UserWay floating launcher; panel still opens via widgetOpen() from footer trigger.
 * @see https://userway.org/docs — iconVisibilityOff, userway:init_completed
 */
function hideUserWayLauncherFromEvent(event) {
  const instance = event?.detail?.userWayInstance;
  if (instance && typeof instance.iconVisibilityOff === 'function') {
    instance.iconVisibilityOff();
    return true;
  }
  if (typeof window.UserWay?.iconVisibilityOff === 'function') {
    window.UserWay.iconVisibilityOff();
    return true;
  }
  return false;
}

document.addEventListener('userway:init_completed', (e) => {
  hideUserWayLauncherFromEvent(e);
});

document.addEventListener('userway:render_completed', (e) => {
  hideUserWayLauncherFromEvent(e);
});

let iconHidePollAttempts = 0;
const iconHidePoll = setInterval(() => {
  iconHidePollAttempts += 1;
  if (typeof window.UserWay?.iconVisibilityOff === 'function') {
    window.UserWay.iconVisibilityOff();
    clearInterval(iconHidePoll);
    return;
  }
  if (iconHidePollAttempts >= ICON_HIDE_MAX_ATTEMPTS) {
    clearInterval(iconHidePoll);
  }
}, ICON_HIDE_POLL_MS);

/**
 * Opens the UserWay widget panel if the API is available.
 * @returns {boolean} True if opened, false if UserWay not ready
 */
function openUserWayPanel() {
  if (
    typeof window.UserWay !== 'undefined' &&
    typeof window.UserWay.widgetOpen === 'function'
  ) {
    window.UserWay.widgetOpen();
    return true;
  }
  return false;
}

/**
 * Attempts to open the panel; if UserWay is not loaded yet, retries once after a short delay.
 */
function tryOpenPanel() {
  if (openUserWayPanel()) return;
  setTimeout(openUserWayPanel, RETRY_DELAY_MS);
}

/**
 * Returns true if the event target is the trigger or inside it.
 * @param {Event} event
 * @returns {boolean}
 */
function isTriggerTarget(event) {
  const target = event.target;
  if (!target || typeof target.closest !== 'function') return false;
  return target.matches(TRIGGER_SELECTOR) || target.closest(TRIGGER_SELECTOR);
}

/**
 * Handles click on the accessibility widget trigger.
 * @param {MouseEvent} event
 */
function handleClick(event) {
  if (!isTriggerTarget(event)) return;
  event.preventDefault();
  tryOpenPanel();
}

/**
 * Handles Enter/Space on the trigger (keyboard activation for role="button").
 * @param {KeyboardEvent} event
 */
function handleKeydown(event) {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  if (!isTriggerTarget(event)) return;
  event.preventDefault();
  tryOpenPanel();
}

document.addEventListener('click', handleClick);
document.addEventListener('keydown', handleKeydown);
